"use client";

import { Card } from "@mui/material";
import { useFormikContext } from "formik";
import { useRef, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { CustomerFormData } from "@/components/screens/CustomerFormPage/types";
import { CustomerFormMessages } from "@/messages/customer/customer-form.messages";
import pxToRem from "@/themes/functions/pxToRem";

type Props = {
  messages: CustomerFormMessages;
};

function LogoImage({ messages }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { values, setFieldValue } = useFormikContext<CustomerFormData>();
  const [isImageFromBackend, setIsImageFromBackend] = useState<boolean>(!!values.image);
  const { image } = values;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];

    if (file) {
      setFieldValue("image", file);
      setIsImageFromBackend(false);
    }
  };

  const handleRemove = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setFieldValue("image", "");
    setIsImageFromBackend(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFile = () => {
    fileInputRef.current?.click();
  };

  const displayImage = () => {
    if (!image) {
      return "";
    }
    
    // If image is a string, it's from backend (base64)
    if (typeof image === "string") {
      // Check if it already contains data:image prefix
      if (image.startsWith('data:image/')) {
        return image;
      }
      // Otherwise, add the base64 prefix
      return `data:image/jpeg;base64,${image}`;
    }

    // If image is a File or Blob object, create object URL
    if (typeof image === "object" && image !== null) {
      try {
        return URL.createObjectURL(image as File);
      } catch (error) {
        console.error("Error creating object URL:", error);
        return "";
      }
    }

    return "";
  };

  const hasImage = !!image;

  return (
    <Card
      sx={{
        "&:hover .card-header": {
          transform: "translate3d(0, -80px, 0)",
          zIndex: 10001,
        },
        "& .card-header": {
          transition: "transform 300ms cubic-bezier(0.34, 1.61, 0.7, 1)",
        },
        "& .button-container": {
          opacity: 0,
          transition: "opacity 100ms ease-out",
        },
        "&:hover .button-container": {
          opacity: 1,
          transitionDelay: "150ms",
        },
        height: "100%",
      }}
      onClick={triggerFile}
    >
      <input type="file" hidden onChange={handleChange} ref={fileInputRef} accept="image/*" />

      <MDBox
        position="relative"
        borderRadius="lg"
        mt={-3}
        mx={2}
        className={hasImage ? "card-header" : ""}
        sx={{ transition: "none" }}
        height="300px"
        bgColor="white"
      >
        <MDBox
          borderRadius="lg"
          shadow="sm"
          width="100%"
          height="100%"
          position="relative"
          zIndex={10}
          mb={2}
          overflow="hidden"
          bgColor="white"
        >
          {hasImage ? (
            <img
              src={displayImage()}
              alt={""}
              style={{
                display: "block",
                objectFit: "contain",
                objectPosition: "center",
                width: "100%",
                height: "100%",
              }}
            />
          ) : (
            <MDBox position="relative" borderRadius="lg" height={"100%"} width="100%">
              <MDBox
                borderRadius="lg"
                shadow="sm"
                width="100%"
                sx={{ height: pxToRem(300) }}
                height="auto"
                zIndex={10}
                display={"flex"}
                bgColor="white"
                justifyContent={"center"}
                alignItems={"center"}
                flexDirection={"column"}
                mb={2}
                overflow="hidden"
              >
                <MDButton
                  variant="gradient"
                  color="dark"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerFile();
                  }}
                >
                  {messages.addImageLabel}
                </MDButton>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <MDBox textAlign="center" pt={2} pb={3} px={3}>
        <MDBox
          display="flex"
          justifyContent="center"
          position="relative"
          alignItems="center"
          mt={-10}
          zIndex={1}
          className="button-container"
        >
          <MDBox mr={1}>
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                triggerFile();
              }}
            >
              {messages.updateImgButton}
            </MDButton>
          </MDBox>

          <MDButton variant="outlined" color="error" size="small" onClick={handleRemove}>
            {messages.removeButton}
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default LogoImage;
